import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { CiSearch } from 'react-icons/ci';
import { MdCategory } from 'react-icons/md';
import { FiUploadCloud, FiEdit2, FiX, FiImage, FiCheckCircle } from 'react-icons/fi';
import { MdDeleteOutline } from 'react-icons/md';
import AdminSidebar from 'admin/sidebar/AdminSidebar';
import DefaultImg from '../../assets/images/DefulatLogoCat.png';
import './ServiceCatalog.css';

const API_BASE_URL = 'https://backend.washta.com';

const ServiceCatalog = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [defaultImageUrl, setDefaultImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [editingItemId, setEditingItemId] = useState('');

  const fetchCatalog = async () => {
    const token = localStorage.getItem('accessTokenAdmin');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/service-catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load service catalog.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCatalog();
  }, []);

  const filteredItems = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const name = String(item?.name || '').toLowerCase();
      const slug = String(item?.slug || '').toLowerCase();
      return name.includes(query) || slug.includes(query);
    });
  }, [items, searchTerm]);

  const uploadImage = async (file) => {
    const token = localStorage.getItem('accessTokenAdmin');
    const formData = new FormData();
    formData.append('file', file);
    setIsUploading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/uploadFile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const uploadedPath = response?.data?.data?.path;
      if (!uploadedPath) throw new Error('Upload failed');
      setDefaultImageUrl(uploadedPath);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Image upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      event.target.value = '';
      return;
    }
    await uploadImage(file);
    event.target.value = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = serviceName.trim();
    if (!trimmedName) {
      toast.error('Service name is required');
      return;
    }
    const token = localStorage.getItem('accessTokenAdmin');
    setSubmitting(true);
    try {
      const payload = {
        name: trimmedName,
        default_image_url: defaultImageUrl.trim() || null,
      };
      const isEditing = Boolean(editingItemId);
      const response = await axios[isEditing ? 'patch' : 'post'](
        `${API_BASE_URL}/api/admin/service-catalog${isEditing ? `/${editingItemId}` : ''}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const savedItem = response?.data?.data;
      if (savedItem) {
        setItems((prev) => {
          if (isEditing) {
            return prev.map((item) => (item?._id === editingItemId ? savedItem : item));
          }
          return [savedItem, ...prev];
        });
      }
      setServiceName('');
      setDefaultImageUrl('');
      setEditingItemId('');
      toast.success(isEditing ? 'Service updated' : 'Service added');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save service');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItemId(item?._id || '');
    setServiceName(item?.name || '');
    setDefaultImageUrl(item?.default_image_url || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (item) => {
    if (!item?._id) return;
    const confirmed = window.confirm(`"${item?.name}" ko delete karna chahte ho?`);
    if (!confirmed) return;
    const token = localStorage.getItem('accessTokenAdmin');
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/service-catalog/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.filter((c) => c?._id !== item._id));
      if (editingItemId === item._id) {
        setServiceName('');
        setDefaultImageUrl('');
        setEditingItemId('');
      }
      toast.success('Service deleted');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to delete service');
    }
  };

  const handleReset = () => {
    setServiceName('');
    setDefaultImageUrl('');
    setEditingItemId('');
  };

  const totalWithImage = items.filter((item) => Boolean(item?.default_image_url)).length;
  const isEditing = Boolean(editingItemId);

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar">
        <AdminSidebar />
      </div>

      <div className="div-Dashbaord">
        <div className="width-settimg addtionwidth-setting service-catalog-page">

          {/* ── Hero ── */}
          <div className="service-catalog-hero">
            <div className="service-catalog-hero-top">
              <div>
                <div className="service-catalog-badge">
                  <MdCategory size={13} />
                  Master Services
                </div>
                <h1 className="service-catalog-title">Service Catalog</h1>
                <p className="service-catalog-subtitle">
                  Sellers ke dropdown mein sirf yehi services dikhti hain. Yahan se service names
                  aur default images manage karo.
                </p>
              </div>
              <div className="service-catalog-stats">
                <div className="service-catalog-stat">
                  <p className="service-catalog-stat-label">Total</p>
                  <p className="service-catalog-stat-value">{items.length}</p>
                </div>
                <div className="service-catalog-stat">
                  <p className="service-catalog-stat-label">With Image</p>
                  <p className="service-catalog-stat-value">{totalWithImage}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Form Card ── */}
          <div className="service-catalog-card">
            <div className="service-catalog-card-header">
              <h2>{isEditing ? 'Edit Service' : 'Add New Service'}</h2>
              <p>
                {isEditing
                  ? 'Fields update karo aur save karo.'
                  : 'Service ka naam aur image daal ke save karo.'}
              </p>
            </div>

            <form className="service-catalog-form" onSubmit={handleSubmit}>
              {isEditing && (
                <div className="service-catalog-edit-banner">
                  <FiEdit2 size={14} />
                  Editing: <strong>{serviceName}</strong>
                </div>
              )}

              <div className="service-catalog-field">
                <label className="service-catalog-label">Service Name</label>
                <input
                  className="service-catalog-input"
                  placeholder="e.g. Carwash, AC Repair…"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  autoFocus={isEditing}
                />
              </div>

              <div className="service-catalog-field">
                <label className="service-catalog-label">Default Image</label>
                <div className="service-catalog-upload">
                  <img
                    className="service-catalog-image"
                    src={defaultImageUrl || DefaultImg}
                    alt="Preview"
                  />
                  <div className="service-catalog-upload-copy">
                    <p className="service-catalog-upload-title">
                      {defaultImageUrl ? 'Image ready' : 'Upload a service image'}
                    </p>
                    <p className="service-catalog-upload-text">
                      Max 5MB. Sellers aur customers ko ye image dikhegi.
                    </p>
                    <label className="service-catalog-secondary-btn" style={{ cursor: 'pointer' }}>
                      <FiUploadCloud size={14} />
                      {isUploading ? 'Uploading…' : 'Choose Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {defaultImageUrl && (
                      <button
                        type="button"
                        onClick={() => setDefaultImageUrl('')}
                        style={{
                          marginLeft: 10,
                          background: 'none',
                          border: 'none',
                          color: '#c13c3c',
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <FiX size={12} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="service-catalog-actions">
                {isEditing && (
                  <button
                    type="button"
                    className="service-catalog-secondary-btn"
                    onClick={handleReset}
                  >
                    <FiX size={13} /> Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="service-catalog-primary-btn"
                  disabled={submitting || isUploading}
                >
                  {submitting
                    ? 'Saving…'
                    : isEditing
                    ? 'Update Service'
                    : '+ Add Service'}
                </button>
              </div>
            </form>
          </div>

          {/* ── List Card ── */}
          <div className="service-catalog-card">
            <div className="service-catalog-list-head">
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 800, color: '#1a1f3c' }}>
                  Existing Services
                  {items.length > 0 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        fontWeight: 700,
                        background: 'rgba(116,126,238,0.1)',
                        color: '#5057c0',
                        borderRadius: 999,
                        padding: '2px 9px',
                        verticalAlign: 'middle',
                      }}
                    >
                      {filteredItems.length}
                    </span>
                  )}
                </h2>
                <p style={{ margin: 0, fontSize: 13, color: '#7078a0' }}>
                  Seller dropdown mein ye services show hongi.
                </p>
              </div>
              <div className="service-catalog-field service-catalog-search" style={{ marginBottom: 0 }}>
                <div style={{ position: 'relative' }}>
                  <CiSearch
                    style={{
                      position: 'absolute',
                      left: 13,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9198b8',
                      fontSize: 18,
                      pointerEvents: 'none',
                    }}
                  />
                  <input
                    className="service-catalog-input"
                    placeholder="Search by name or slug…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: 40 }}
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="service-catalog-empty">Loading…</div>
            ) : filteredItems.length === 0 ? (
              <div className="service-catalog-empty">
                {searchTerm ? `"${searchTerm}" ke liye koi service nahi mili.` : 'Abhi koi service nahi hai. Upar se add karo.'}
              </div>
            ) : (
              <div className="service-catalog-grid">
                {filteredItems.map((item) => (
                  <div
                    className="service-catalog-item"
                    key={item._id || item.slug || item.name}
                    style={editingItemId === item._id ? { border: '2px solid #6b5fe4' } : {}}
                  >
                    <img
                      className="service-catalog-item-image"
                      src={item?.default_image_url || DefaultImg}
                      alt={item?.name || 'Service'}
                    />
                    <div className="service-catalog-item-body">
                      <h3 className="service-catalog-item-name">{item?.name}</h3>
                      <p className="service-catalog-item-slug">{item?.slug || '—'}</p>
                      <div
                        className={`service-catalog-item-status ${
                          item?.default_image_url ? 'has-image' : 'no-image'
                        }`}
                      >
                        {item?.default_image_url ? (
                          <>
                            <FiCheckCircle size={11} /> Image added
                          </>
                        ) : (
                          <>
                            <FiImage size={11} /> No image
                          </>
                        )}
                      </div>
                      <div className="service-catalog-item-actions">
                        <button
                          type="button"
                          className="service-catalog-item-btn"
                          onClick={() => handleEdit(item)}
                        >
                          <FiEdit2 size={12} /> Edit
                        </button>
                        <button
                          type="button"
                          className="service-catalog-item-btn danger"
                          onClick={() => handleDelete(item)}
                        >
                          <MdDeleteOutline size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default ServiceCatalog;